import { useCallback, useEffect, useRef, useState } from "react";
import { api, apiError, uploadFile, ocrScan } from "@/lib/api";
import { compressImage } from "@/lib/image";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatCard } from "@/components/shared/PagePrimitives";
import { SignedImg } from "@/components/shared/SignedImg";
import { SettleOverlay } from "@/components/cashbox/SettleOverlay";
import { formatCZK, formatDate } from "@/lib/format";
import { Banknote, Receipt, Scale, Check, Upload, Trash2, X, Pencil } from "lucide-react";
import { toast } from "sonner";
import { ask } from "@/lib/confirm";
import { ocrCurrency, foreignAmount } from "@/lib/documents";

const STEPS = (type) => [
  { step: 1, label: type === "zaloha" ? "Záloha vydána" : "Výdaj vydán" },
  { step: 2, label: "Doklady nahrány zaměstnancem" },
  { step: 3, label: "Čeká na zpracování asistentkou" },
  { step: 4, label: "Vyúčtování schváleno" },
  { step: 5, label: type === "zaloha" ? "Záloha uzavřena" : "Výdaj uzavřen" },
];

// Detail případu: desktop boční panel zprava, mobil spodní sheet.
export function CaseDetailPanel({ caseId, open, onClose, onChanged, admin, autoSettle }) {
  const { company, user } = useAuth();
  const canClose = ["OWNER", "ASSISTANT", "ACCOUNTANT"].includes(user?.role);
  const cats = company?.receipt_categories?.length ? company.receipt_categories : ["Materiál"];
  const [d, setD] = useState(null);
  const [cat, setCat] = useState("");
  const [busy, setBusy] = useState(false);
  const [settleOpen, setSettleOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ amount: "", purpose: "", date: "" });
  const [editBusy, setEditBusy] = useState(false);
  const [mcOpen, setMcOpen] = useState(false);
  const [mcForm, setMcForm] = useState({ returned: "", category: "", project_id: "rezie" });
  const [mcBusy, setMcBusy] = useState(false);
  const [projects, setProjects] = useState([]);
  const [desktop, setDesktop] = useState(typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches);
  const fileRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const on = (e) => setDesktop(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  const load = useCallback(async () => {
    if (!caseId) return;
    try {
      const { data } = await api.get(`/cashbox/cases/${caseId}`);
      setD(data);
    } catch (e) { toast.error(apiError(e)); onClose(); }
  }, [caseId, onClose]);

  useEffect(() => {
    setD(null); setSettleOpen(false);
    if (caseId) load();
  }, [caseId, load]);
  useEffect(() => { setCat(cats[0] || ""); }, [caseId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (autoSettle && d && d.status === "vyporadani") setSettleOpen(true);
  }, [autoSettle, d]);

  const reload = () => { load(); onChanged?.(); };

  const uploadReceipt = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const url = await uploadFile(await compressImage(file));
      const data = await ocrScan("receipt", url);
      // 324: účtenka v cizí měně (tankování v Rakousku) — server ji převede kurzem ČNB k datu účtenky
      const { data: saved } = await api.post("/receipts", {
        file_url: url, supplier_name: data.supplier_name, date: (data.date || "").slice(0, 10),
        total: Number(data.total) || 0, vat_amount: Number(data.vat_amount) || 0,
        payment_method: "Hotovost", category: cat,
        project_id: d.project_id || null, items: data.items || [],
        cash_case_id: d.id, currency: ocrCurrency(data.currency),
      });
      const fx = foreignAmount(saved);
      toast.success(fx ? `Účtenka připojena k případu — ${fx.text} = ${formatCZK(saved.total)} (${fx.rate}).` : "Účtenka připojena k případu.");
      reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const detach = async (rid) => {
    try {
      await api.delete(`/cashbox/cases/${d.id}/receipts/${rid}`);
      toast.success("Účtenka odpojena — v Dokladech zůstává.");
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  const runAction = async () => {
    const a = d?.action;
    if (!a) return;
    if (a.key === "add_receipt") { fileRef.current?.click(); return; }
    if (a.key === "to_review" || a.key === "approve") {
      setBusy(true);
      try {
        await api.post(`/cashbox/cases/${d.id}/advance`, { to: a.key === "to_review" ? "kontrola" : "vyporadani" });
        toast.success(a.key === "to_review" ? "Předáno asistentce." : "Vyúčtování schváleno.");
        reload();
      } catch (e) { toast.error(apiError(e)); }
      finally { setBusy(false); }
      return;
    }
    setSettleOpen(true);
  };

  const removeCase = async () => {
    if (!await ask(`Stornovat ${d.human_id}? Smaže se případ i výdajový pohyb v knize.`)) return;
    try {
      await api.delete(`/cashbox/cases/${d.id}`);
      toast.success("Případ stornován.");
      onChanged?.(); onClose();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openEdit = () => {
    setEditForm({ amount: String(d.amount_issued ?? ""), purpose: d.purpose || "", date: (d.issued_date || "").slice(0, 10) });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    if (!(Number(editForm.amount) > 0) || !editForm.purpose.trim()) return;
    setEditBusy(true);
    try {
      await api.patch(`/cashbox/cases/${d.id}`, {
        amount: Number(editForm.amount), purpose: editForm.purpose.trim(), date: editForm.date || null });
      toast.success("Případ upraven.");
      setEditOpen(false); reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setEditBusy(false); }
  };

  const reached = new Set((d?.timeline || []).map((t) => t.step));
  const byStep = Object.fromEntries((d?.timeline || []).map((t) => [t.step, t]));
  const closed = d?.status === "uzavreno";
  const manualClosed = !!d?.closed_manually;

  // Ruční uzavření — „Zbývá nedoloženo" se přepočítává při psaní.
  const mcRest = d ? Math.round(((d.amount_issued || 0) - (d.documented || 0) - (Number(mcForm.returned) || 0)) * 100) / 100 : 0;
  const openManualClose = async () => {
    setMcForm({ returned: "", category: cats[0] || "", project_id: d?.project_id || "rezie" });
    setMcOpen(true);
    try { const { data } = await api.get("/projects/options"); setProjects(data || []); } catch { setProjects([]); }
  };
  const submitManualClose = async () => {
    setMcBusy(true);
    try {
      const body = { returned: Number(mcForm.returned) || 0 };
      if (mcRest > 0) {
        body.category = mcForm.category;
        body.project_id = mcForm.project_id === "rezie" ? null : mcForm.project_id;
      }
      await api.post(`/cashbox/cases/${d.id}/close-manual`, body);
      toast.success("Případ uzavřen ručně.");
      setMcOpen(false); reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setMcBusy(false); }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
        <SheetContent side={desktop ? "right" : "bottom"}
          className={desktop ? "w-[min(600px,100%)] sm:max-w-[600px] overflow-y-auto" :
            "rounded-t-[24px] max-h-[92%] overflow-y-auto pb-[env(safe-area-inset-bottom)]"}
          data-testid="case-panel">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 flex-wrap">
              {d ? <>{d.human_id} <span className="text-muted-foreground font-semibold text-sm">· {d.type === "zaloha" ? "Záloha" : "Přímý výdaj"}</span>
                {manualClosed && <span className="px-2 py-0.5 rounded-full bg-brand/15 text-brand text-[11px] font-bold" data-testid="case-manual-badge">Uzavřeno ručně</span>}
              </> : "Načítám…"}
            </SheetTitle>
            <SheetDescription className="sr-only">Detail případu pokladny</SheetDescription>
          </SheetHeader>
          {d && (
            <div className="space-y-4 mt-3">
              <div className="text-sm">
                <div className="font-bold text-[16px]" data-testid="case-person">{d.person_name}</div>
                <div className="text-muted-foreground mt-0.5">{d.purpose}</div>
                <div className="text-muted-foreground text-[13px] mt-0.5">
                  Vydal {d.issued_by_name} · {formatDate(d.issued_date)}
                  {d.project_name && <> · {d.project_name}</>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <StatCard icon={Banknote} label="Vydáno" value={formatCZK(d.amount_issued)}
                  title={formatCZK(d.amount_issued)} valueClass="!text-[22px]" testid="case-issued" />
                <StatCard icon={Receipt} label="Doklady" value={formatCZK(d.documented)}
                  title={formatCZK(d.documented)} valueClass="!text-[22px]" testid="case-documented" />
                <StatCard icon={Scale} label="Rozdíl" value={formatCZK(d.diff)} title={formatCZK(d.diff)}
                  valueClass={`!text-[22px] ${d.diff < 0 ? "text-brand" : d.diff > 0 ? "text-destructive" : ""}`}
                  testid="case-diff" />
              </div>

              {/* Timeline — 5 pevných kroků, nedosažené dorenderujeme jako „čeká". */}
              <div className="bg-secondary/60 border border-border rounded-[14px] p-3.5 space-y-2.5" data-testid="case-timeline">
                {STEPS(d.type).map((s) => {
                  const done = reached.has(s.step);
                  const t = byStep[s.step];
                  const label = s.step === 5 && t?.key === "uzavreno_rucne" ? "Uzavřeno ručně" : s.label;
                  return (
                    <div key={s.step} className="flex items-start gap-2.5">
                      <span className={`w-5 h-5 rounded-full grid place-items-center shrink-0 mt-0.5 ${
                        done ? "bg-success text-white" : "bg-card border border-border text-muted-foreground"}`}>
                        {done ? <Check size={12} /> : <span className="text-[10px] font-bold">{s.step}</span>}
                      </span>
                      <div className="min-w-0">
                        <div className={`text-sm font-semibold ${done ? "" : "text-muted-foreground"}`}>{label}</div>
                        {t && <div className="text-[12px] text-muted-foreground">{formatDate(t.at?.slice(0, 10))} · {t.by_name}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div>
                <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-2">
                  Účtenky ({d.receipts.length})
                </div>
                {d.receipts.length === 0 && (
                  <p className="text-sm text-muted-foreground" data-testid="case-no-receipts">
                    Zatím žádné doklady — nahrajte první účtenku.
                  </p>
                )}
                <div className="space-y-2">
                  {d.receipts.map((r) => (
                    <div key={r.id} className="flex items-center gap-3 bg-card border border-border rounded-[12px] p-2.5" data-testid={`case-receipt-${r.id}`}>
                      {r.file_url && <SignedImg url={r.file_url} alt="účtenka" thumb={40} className="w-10 h-10 object-cover rounded-md border border-border shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{r.supplier_name || "Účtenka"}</div>
                        <div className="text-[12px] text-muted-foreground">{formatDate(r.date)}</div>
                      </div>
                      <div className="font-bold tabular-nums whitespace-nowrap">{formatCZK(r.total)}</div>
                      {admin && !closed && (
                        <button onClick={() => detach(r.id)} title="Odpojit od případu" data-testid={`case-receipt-detach-${r.id}`}
                          className="text-muted-foreground hover:text-destructive shrink-0"><X size={15} /></button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {admin && !closed && (
                <>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden"
                    onChange={(e) => uploadReceipt(e.target.files?.[0])} data-testid="case-receipt-input" />
                  <div className="bg-secondary/60 border border-border rounded-[14px] p-3.5 space-y-2.5">
                    <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Nahrát účtenku</div>
                    <Select value={cat} onValueChange={setCat}>
                      <SelectTrigger className="h-11 w-full bg-card" data-testid="case-receipt-category">
                        <SelectValue placeholder="Kategorie výdaje" />
                      </SelectTrigger>
                      <SelectContent>
                        {cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {busy ? (
                      <div className="flex items-center gap-3 py-2" data-testid="case-receipt-ocr">
                        <div className="w-6 h-6 rounded-full border-[3px] border-secondary border-t-brand animate-spin motion-reduce:animate-none" />
                        <span className="text-sm text-muted-foreground">OCR čte účtenku…</span>
                      </div>
                    ) : (
                      <Button variant="outline" onClick={() => fileRef.current?.click()}
                        className="h-11 w-full font-semibold" data-testid="case-upload-receipt">
                        <Upload size={15} /> Vybrat fotku účtenky
                      </Button>
                    )}
                  </div>
                </>
              )}

              {closed && manualClosed && (
                <div className="bg-secondary/60 border border-border rounded-[14px] p-3.5" data-testid="case-manual-recap">
                  <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Ruční uzavření</div>
                  <div className="text-sm space-y-1">
                    {(d.manual_return?.amount || 0) > 0 && <div>Vráceno hotově: <b>{formatCZK(d.manual_return.amount)}</b></div>}
                    {(d.manual_writeoff?.amount || 0) > 0 && <div>Nedoloženo — náklad ({d.manual_writeoff.category}{d.manual_writeoff.project_id ? "" : " · režie"}): <b>{formatCZK(d.manual_writeoff.amount)}</b></div>}
                  </div>
                  <div className="text-[12px] text-muted-foreground mt-1.5">Uzavřel/a {d.closed_by} · {formatDate(d.closed_at?.slice(0, 10))}</div>
                </div>
              )}

              {closed && d.settlement && (
                <div className="bg-secondary/60 border border-border rounded-[14px] p-3.5" data-testid="case-settlement">
                  <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Vypořádání</div>
                  <div className="text-sm">
                    {d.settlement.kind === "vraceni" && <>Převzato zpět <b>{formatCZK(d.settlement.amount)}</b></>}
                    {d.settlement.kind === "doplatek" && <>Vyplaceno <b>{formatCZK(d.settlement.amount)}</b></>}
                    {d.settlement.kind === "bez_pohybu" && <>Uzavřeno bez pohybu hotovosti</>}
                  </div>
                  {d.settlement.signature_url && (
                    <SignedImg url={d.settlement.signature_url} alt="podpis"
                      className="w-full max-h-32 object-contain bg-white rounded-[10px] border border-border mt-2" testid="case-signature" />
                  )}
                  <div className="text-[12px] text-muted-foreground mt-1.5">
                    Podepsal/a {d.settlement.signed_name} · {formatDate(d.settlement.signed_at?.slice(0, 10))} · pokladník {d.settlement.cashier_name}
                  </div>
                </div>
              )}

              {admin && !closed && d.action && (
                <Button onClick={runAction} disabled={busy}
                  variant={d.action.variant === "primary" ? "default" : "outline"}
                  className="h-12 w-full font-bold" data-testid="case-action">
                  {d.action.label}
                </Button>
              )}
              {canClose && !closed && (
                <Button variant="outline" onClick={openManualClose} data-testid="case-manual-close-btn"
                  className="h-11 w-full font-bold">
                  Uzavřít bez účtenek…
                </Button>
              )}
              {admin && !closed && d.status === "vydano" && d.receipts.length === 0 && (
                <Button variant="outline" onClick={openEdit} data-testid="case-edit"
                  className="h-11 w-full font-bold">
                  <Pencil size={15} /> Upravit částku / účel
                </Button>
              )}
              {admin && !closed && d.receipts.length === 0 && (
                <button onClick={removeCase} data-testid="case-delete"
                  className="w-full text-center text-[13px] font-semibold text-muted-foreground hover:text-destructive flex items-center justify-center gap-1.5">
                  <Trash2 size={13} /> Stornovat případ (překlep)
                </button>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
      <SettleOverlay open={settleOpen} caseData={d} onClose={() => setSettleOpen(false)}
        onDone={() => { setSettleOpen(false); reload(); }} />
      <Dialog open={mcOpen} onOpenChange={setMcOpen}>
        <DialogContent className="max-w-md" data-testid="case-manual-close-dialog">
          <DialogHeader>
            <DialogTitle>Uzavřít bez účtenek</DialogTitle>
            <DialogDescription className="sr-only">Ruční vypořádání případu</DialogDescription>
          </DialogHeader>
          {d && (
            <div className="space-y-3">
              <div>
                <Label>Kolik se vrátilo do pokladny? (Kč)</Label>
                <Input type="number" min="0" inputMode="decimal" value={mcForm.returned}
                  onChange={(e) => setMcForm({ ...mcForm, returned: e.target.value })}
                  className="h-12 mt-1 w-full tabular-nums" data-testid="mc-returned" autoFocus />
              </div>
              {mcRest > 0 ? (
                <div className="space-y-2.5">
                  <p className="text-sm font-semibold" data-testid="mc-rest-line">
                    Zbývá nedoloženo: <span className="text-brand">{formatCZK(mcRest)}</span>
                  </p>
                  <p className="text-sm text-muted-foreground">Zbytek se zaúčtuje jako náklad firmy.</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Select value={mcForm.category} onValueChange={(v) => setMcForm({ ...mcForm, category: v })}>
                      <SelectTrigger className="h-10" data-testid="mc-category"><SelectValue placeholder="Kategorie" /></SelectTrigger>
                      <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={mcForm.project_id} onValueChange={(v) => setMcForm({ ...mcForm, project_id: v })}>
                      <SelectTrigger className="h-10" data-testid="mc-project"><SelectValue placeholder="Zakázka / Režie" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="rezie">Režie (bez zakázky)</SelectItem>
                        {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground" data-testid="mc-rest-zero">Vše sedí — nedoloženo 0 Kč, případ se uzavře rovnou.</p>
              )}
              <p className="text-xs text-muted-foreground" data-testid="mc-wage-hint">
                Záloha na výplatu? Nech případ otevřený, dokud peníze nepřinese — pak sem zapiš vrácenou částku a případ se uzavře.
              </p>
              <div className="flex flex-col-reverse md:flex-row md:justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => setMcOpen(false)} className="h-11 w-full md:w-auto" data-testid="mc-cancel">Zrušit</Button>
                <Button onClick={submitManualClose}
                  disabled={mcBusy || mcRest < 0 || (mcRest > 0 && !mcForm.category)}
                  className="h-11 w-full md:w-auto font-bold" data-testid="mc-submit">
                  {mcBusy ? "Ukládám…" : "Uzavřít případ"}
                </Button>
              </div>
              {mcRest < 0 && <p className="text-xs text-destructive">Vráceno víc, než bylo vydáno po odečtení účtenek.</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-sm" data-testid="case-edit-dialog">
          <DialogHeader>
            <DialogTitle>Upravit částku / účel</DialogTitle>
            <DialogDescription className="sr-only">Úprava vydaného případu bez dokladů</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Částka (Kč)</Label>
              <Input type="number" min="0" inputMode="decimal" value={editForm.amount}
                onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                className="h-12 mt-1 w-full tabular-nums" data-testid="case-edit-amount" autoFocus />
            </div>
            <div>
              <Label>Účel</Label>
              <Input value={editForm.purpose} onChange={(e) => setEditForm({ ...editForm, purpose: e.target.value })}
                className="h-12 mt-1 w-full" data-testid="case-edit-purpose" />
            </div>
            <div>
              <Label>Datum</Label>
              <Input type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                className="h-12 mt-1 w-full" data-testid="case-edit-date" />
            </div>
            <div className="flex flex-col-reverse md:flex-row md:justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setEditOpen(false)} className="h-11 w-full md:w-auto" data-testid="case-edit-cancel">Zrušit</Button>
              <Button onClick={saveEdit} disabled={editBusy || !(Number(editForm.amount) > 0) || !editForm.purpose.trim()}
                className="h-11 w-full md:w-auto font-bold" data-testid="case-edit-save">
                {editBusy ? "Ukládám…" : "Uložit změny"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
